import {LoadingDots} from '@wandb/weave/components/LoadingDots';
import React, {useEffect, useMemo, useState} from 'react';

import {NotApplicable} from '../../../../NotApplicable';
import {DenseCellValue} from './ExampleCompareSectionTable';
import {OutputCachingManager, OutputLoadEvent} from './useOutputCachingManager';

const useOutputCellState = (
  manager: OutputCachingManager,
  perdictCallId: string | undefined
) => {
  const [status, setStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>(
    'idle'
  );
  const [value, setValue] = useState<any>(undefined);

  useEffect(() => {
    const unsub = manager.eventObserver.subscribe((evt: OutputLoadEvent) => {
      if (evt.callId !== perdictCallId) {
        return;
      }
      if (evt.status === 'loaded') {
        setStatus('loaded');
        setValue(evt.data);
      } else if (evt.status === 'loading') {
        setStatus('loading');
      } else if (evt.status === 'error') {
        setStatus('error');
      }
    });
    if (perdictCallId) {
      manager.requestCallOutput(perdictCallId);
    }

    return () => {
      unsub();
    };
  }, [perdictCallId, manager]);

  return useMemo(() => ({status, value}), [status, value]);
};

type ExampleCompareOutputCellProps = {
  manager: OutputCachingManager;
  perdictCallIds: Record<string, string | undefined>;
  evaluationCallId: string;
  lineClamp: number;
};

export const ExampleCompareOutputCell: React.FC<
  ExampleCompareOutputCellProps
> = ({manager, evaluationCallId, lineClamp, perdictCallIds}) => {
  const predictCallId = perdictCallIds[evaluationCallId];

  const {status, value} = useOutputCellState(manager, predictCallId);

  if (status === 'loading' && value === undefined) {
    return (
      <div className="flex items-center h-full">
        <LoadingDots />
      </div>
    );
  }
  if (status === 'error') {
    return <NotApplicable />;
  }
  // In the original, summary shows first trial; same rendering suffices here
  return <DenseCellValue value={value} lineClamp={lineClamp} />;
};
